import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { InfraAPI, ROOM_TYPES } from "@/lib/infra-api";
import { SlideOver, SaveStatus, Section } from "./SlideOver";
import { RoomLayoutEditor } from "./RoomLayoutEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, ChevronDown, ChevronRight, DoorOpen,
  Plus, Trash2, Sparkles, Loader2, Search, Grid3x3, Upload, Download,
} from "lucide-react";
import { Layers } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Buildings panel — hierarchical tree editor for Campus → Building → Wing →
 * Floor → Room. Optimised for zero-training use: everything is inline-add,
 * click-to-expand and inline-delete. Bulk room generation from a floor
 * accelerates initial setup.
 */
export function BuildingsPanel({ open, onClose, campusId, campusLabel }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(new Set());
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("idle");
  const [layoutRoom, setLayoutRoom] = useState(null);

  const buildingsQ = useQuery({
    queryKey: ["infra-buildings", campusId],
    queryFn: () => InfraAPI.listBuildings(campusId),
    enabled: !!campusId && open,
  });
  const floorsQ = useQuery({
    queryKey: ["infra-floors", campusId],
    queryFn: () => InfraAPI.listFloors(campusId),
    enabled: !!campusId && open,
  });
  const roomsQ = useQuery({
    queryKey: ["infra-rooms", campusId],
    queryFn: () => InfraAPI.listRooms(campusId),
    enabled: !!campusId && open,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["infra-buildings", campusId] });
    qc.invalidateQueries({ queryKey: ["infra-floors", campusId] });
    qc.invalidateQueries({ queryKey: ["infra-rooms", campusId] });
    qc.invalidateQueries({ queryKey: ["infra-dashboard", campusId] });
  };

  const wrap = async (fn, okMsg) => {
    setStatus("saving");
    try {
      await fn();
      setStatus("saved");
      okMsg && toast.success(okMsg);
      invalidate();
      setTimeout(() => setStatus("idle"), 1200);
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
  };

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const isOpen = (id) => expanded.has(id);

  const buildings = buildingsQ.data || [];
  const floorsByBuilding = useMemo(() => {
    const m = new Map();
    for (const f of floorsQ.data || []) {
      const arr = m.get(String(f.building_id)) || [];
      arr.push(f);
      m.set(String(f.building_id), arr);
    }
    return m;
  }, [floorsQ.data]);
  const roomsByFloor = useMemo(() => {
    const m = new Map();
    for (const r of roomsQ.data || []) {
      const arr = m.get(String(r.floor_id)) || [];
      arr.push(r);
      m.set(String(r.floor_id), arr);
    }
    return m;
  }, [roomsQ.data]);

  const searchQ = q.trim().toLowerCase();
  const matches = (s) => !searchQ || (s || "").toLowerCase().includes(searchQ);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Buildings & Rooms"
      subtitle={`Configure the hierarchy under ${campusLabel || "this campus"}.`}
      right={<SaveStatus status={status} />}
      width="max-w-4xl"
      testid="buildings"
    >
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search buildings, floors, rooms…"
              className="pl-9"
              data-testid="bld-search"
            />
          </div>
          <ImportRoomsButton campusId={campusId} onImported={invalidate} setStatus={setStatus} />
          <AddBuildingButton campusId={campusId} onCreated={invalidate} setStatus={setStatus} />
        </div>

        {buildingsQ.isLoading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : buildings.length === 0 ? (
          <EmptyBuildings campusId={campusId} onCreated={invalidate} setStatus={setStatus} />
        ) : (
          <ul className="space-y-2" data-testid="buildings-tree">
            {buildings.map((b) => (
              <li key={b.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-3 py-2.5 flex items-center gap-2 hover:bg-slate-50">
                  <button type="button" onClick={() => toggle(b.id)} className="text-slate-400 shrink-0" data-testid={`bld-toggle-${b.id}`}>
                    {isOpen(b.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <Building2 className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{b.name}</div>
                    <div className="text-xs text-slate-500">
                      {b.code || "—"} · {b.building_type || "building"} · {(floorsByBuilding.get(String(b.id)) || []).length} floors
                    </div>
                  </div>
                  <AddFloorButton building={b} onCreated={invalidate} setStatus={setStatus} />
                  <IconTrash onClick={() => wrap(() => InfraAPI.deleteBuilding(b.id), "Building removed")} testid={`bld-del-${b.id}`} />
                </div>

                {isOpen(b.id) && (
                  <ul className="border-t border-slate-100 bg-slate-50/50 divide-y divide-slate-100">
                    {(floorsByBuilding.get(String(b.id)) || []).length === 0 && (
                      <li className="px-6 py-3 text-xs text-slate-500 italic">No floors yet — click “+ Floor” above to add one.</li>
                    )}
                    {(floorsByBuilding.get(String(b.id)) || []).map((f) => (
                      <li key={f.id}>
                        <div className="pl-6 pr-3 py-2 flex items-center gap-2">
                          <button type="button" onClick={() => toggle(f.id)} className="text-slate-400 shrink-0">
                            {isOpen(f.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <div className="h-7 w-7 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                            <Layers className="h-3.5 w-3.5 text-slate-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-800">
                              {f.name} <span className="text-slate-400">· Floor {f.floor_number}</span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {(roomsByFloor.get(String(f.id)) || []).length} rooms
                            </div>
                          </div>
                          <AddRoomButton floor={f} onCreated={invalidate} setStatus={setStatus} />
                          <BulkGenerateButton floor={f} onCreated={invalidate} setStatus={setStatus} />
                          <IconTrash onClick={() => wrap(() => InfraAPI.deleteFloor(f.id), "Floor removed")} testid={`fl-del-${f.id}`} />
                        </div>

                        {isOpen(f.id) && (
                          <ul className="pl-16 pr-3 pb-3 space-y-1">
                            {(roomsByFloor.get(String(f.id)) || [])
                              .filter((r) => matches(r.name) || matches(r.room_number) || matches(r.room_type))
                              .map((r) => (
                                <li key={r.id} className="px-3 py-1.5 rounded-lg bg-white border border-slate-100 flex items-center gap-2 hover:border-slate-200">
                                  <DoorOpen className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-slate-800 truncate">
                                      {r.name} <span className="text-slate-400 text-xs">({r.room_type.replace(/_/g, " ")})</span>
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                      cap {r.capacity || "—"} · exam {r.exam_capacity || 0} · {r.availability || "available"}
                                      {r.layout?.seat_count ? ` · ${r.layout.seat_count} seat layout` : ""}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setLayoutRoom(r)}
                                    className="h-8 w-8 rounded-md hover:bg-violet-50 text-slate-400 hover:text-violet-600 flex items-center justify-center shrink-0"
                                    title="Edit seating layout"
                                    data-testid={`rm-layout-${r.id}`}
                                  >
                                    <Grid3x3 className="h-3.5 w-3.5" />
                                  </button>
                                  <IconTrash onClick={() => wrap(() => InfraAPI.deleteRoom(r.id), "Room removed")} testid={`rm-del-${r.id}`} />
                                </li>
                              ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Room layout editor — opens when a room's grid icon is clicked. */}
      <RoomLayoutEditor
        open={!!layoutRoom}
        room={layoutRoom}
        onClose={() => setLayoutRoom(null)}
        onSaved={invalidate}
      />
    </SlideOver>
  );
}

function ImportRoomsButton({ campusId, onImported, setStatus }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setStatus("saving");
    try {
      const res = await InfraAPI.importRooms(campusId, file);
      setStatus("saved");
      if (res.errors?.length) {
        toast.warning(`${res.created} created · ${res.skipped} skipped · ${res.errors.length} errors`);
        console.warn("Import errors", res.errors);
      } else {
        toast.success(`Imported ${res.created} rooms`);
      }
      onImported();
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-1">
      <a
        href={InfraAPI.templateUrl("rooms")}
        target="_blank"
        rel="noreferrer"
        className="text-xs px-2 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 inline-flex items-center gap-1"
        title="Download CSV template"
        data-testid="rooms-template"
      >
        <Download className="h-3.5 w-3.5" /> Template
      </a>
      <label className={cn(
        "text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 inline-flex items-center gap-1 cursor-pointer",
        busy && "opacity-50 cursor-not-allowed"
      )} data-testid="rooms-import-btn">
        <Upload className="h-3.5 w-3.5" /> {busy ? "Importing…" : "Import CSV"}
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} disabled={busy} />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function IconTrash({ onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center shrink-0"
      title="Delete"
      data-testid={testid}
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function EmptyBuildings({ campusId, onCreated, setStatus }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 flex flex-col items-center justify-center text-center px-6">
      <div className="h-14 w-14 rounded-xl bg-indigo-50 flex items-center justify-center mb-3">
        <Building2 className="h-6 w-6 text-indigo-600" />
      </div>
      <div className="text-base font-semibold text-slate-900">No buildings yet</div>
      <div className="text-sm text-slate-500 mt-1 max-w-md">
        Start by adding your first building. You can add floors and auto-generate rooms with a single click.
      </div>
      <div className="mt-4">
        <AddBuildingButton campusId={campusId} onCreated={onCreated} setStatus={setStatus} />
      </div>
    </div>
  );
}

function AddBuildingButton({ campusId, onCreated, setStatus }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", building_type: "academic", total_floors: 1, has_lift: false });
  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    setStatus("saving");
    try {
      await InfraAPI.createBuilding({ campus_id: campusId, ...form });
      toast.success("Building added");
      setForm({ name: "", code: "", building_type: "academic", total_floors: 1, has_lift: false });
      setOpen(false);
      setStatus("saved");
      onCreated();
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Could not add");
    }
  };
  return (
    <div className="relative">
      <Button size="sm" onClick={() => setOpen((v) => !v)} data-testid="add-building-btn">
        <Plus className="h-4 w-4 mr-1" /> Building
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl p-4 space-y-3 z-10">
          <div className="text-sm font-semibold">New Building</div>
          <Input placeholder="Name (e.g. Main Block)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="new-bld-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            <Input type="number" placeholder="Floors" value={form.total_floors} onChange={(e) => setForm((f) => ({ ...f, total_floors: Number(e.target.value) || 0 }))} />
          </div>
          <Select value={form.building_type} onValueChange={(v) => setForm((f) => ({ ...f, building_type: v }))}>
            <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              {["academic", "admin", "hostel", "sports", "mixed", "utility"].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} data-testid="new-bld-save">Add</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddFloorButton({ building, onCreated, setStatus }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "Ground Floor", floor_number: 0 });
  const submit = async () => {
    setStatus("saving");
    try {
      await InfraAPI.createFloor({ building_id: building.id, ...form });
      toast.success("Floor added");
      setOpen(false);
      setForm({ name: "Floor", floor_number: 1 });
      setStatus("saved");
      onCreated();
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Could not add");
    }
  };
  return (
    <div className="relative">
      <button type="button" className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={() => setOpen((v) => !v)} data-testid={`add-floor-${building.id}`}>
        + Floor
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl border border-slate-200 shadow-xl p-3 space-y-2 z-10">
          <div className="text-xs font-semibold text-slate-600">New Floor · {building.name}</div>
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Input type="number" placeholder="Floor number" value={form.floor_number} onChange={(e) => setForm((f) => ({ ...f, floor_number: Number(e.target.value) || 0 }))} />
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submit}>Add</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddRoomButton({ floor, onCreated, setStatus }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", room_number: "", room_type: "classroom", capacity: 40, exam_capacity: 30 });
  const submit = async () => {
    if (!form.name) return toast.error("Room name required");
    setStatus("saving");
    try {
      await InfraAPI.createRoom({
        building_id: floor.building_id,
        wing_id: floor.wing_id || null,
        floor_id: floor.id,
        ...form,
      });
      toast.success("Room added");
      setForm({ name: "", room_number: "", room_type: "classroom", capacity: 40, exam_capacity: 30 });
      setOpen(false);
      setStatus("saved");
      onCreated();
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Could not add");
    }
  };
  return (
    <div className="relative">
      <button type="button" className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={() => setOpen((v) => !v)} data-testid={`add-room-${floor.id}`}>
        + Room
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl p-3 space-y-2 z-10">
          <div className="text-xs font-semibold text-slate-600">New Room · {floor.name}</div>
          <Input placeholder="Name (e.g. Room 101)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="new-room-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Number" value={form.room_number} onChange={(e) => setForm((f) => ({ ...f, room_number: e.target.value }))} />
            <Select value={form.room_type} onValueChange={(v) => setForm((f) => ({ ...f, room_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROOM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: Number(e.target.value) || 0 }))} />
            <Input type="number" placeholder="Exam Capacity" value={form.exam_capacity} onChange={(e) => setForm((f) => ({ ...f, exam_capacity: Number(e.target.value) || 0 }))} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} data-testid="new-room-save">Add</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BulkGenerateButton({ floor, onCreated, setStatus }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ prefix: "", start_number: 101, count: 10, room_type: "classroom", capacity: 40, exam_capacity: 30 });
  const submit = async () => {
    setStatus("saving");
    try {
      const res = await InfraAPI.bulkGenerateRooms({ floor_id: floor.id, ...form });
      toast.success(`Generated ${res.created} rooms`);
      setOpen(false);
      setStatus("saved");
      onCreated();
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Could not generate");
    }
  };
  return (
    <div className="relative">
      <button
        type="button"
        className="text-xs px-2 py-1 rounded-md bg-violet-50 text-violet-700 hover:bg-violet-100 inline-flex items-center gap-1"
        onClick={() => setOpen((v) => !v)}
        data-testid={`bulk-room-${floor.id}`}
      >
        <Sparkles className="h-3 w-3" /> Bulk
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl p-3 space-y-2 z-10">
          <div className="text-xs font-semibold text-slate-600">Auto-generate rooms on {floor.name}</div>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Prefix" value={form.prefix} onChange={(e) => setForm((f) => ({ ...f, prefix: e.target.value }))} data-testid="bulk-prefix" />
            <Input type="number" placeholder="Start" value={form.start_number} onChange={(e) => setForm((f) => ({ ...f, start_number: Number(e.target.value) || 1 }))} data-testid="bulk-start" />
            <Input type="number" placeholder="Count" value={form.count} onChange={(e) => setForm((f) => ({ ...f, count: Number(e.target.value) || 1 }))} data-testid="bulk-count" />
          </div>
          <Select value={form.room_type} onValueChange={(v) => setForm((f) => ({ ...f, room_type: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROOM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: Number(e.target.value) || 0 }))} />
            <Input type="number" placeholder="Exam Capacity" value={form.exam_capacity} onChange={(e) => setForm((f) => ({ ...f, exam_capacity: Number(e.target.value) || 0 }))} />
          </div>
          <div className="text-[11px] text-slate-500">
            Will create <b>{form.count}</b> rooms named <code>Room {form.prefix}{form.start_number}</code> … <code>Room {form.prefix}{form.start_number + form.count - 1}</code>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} data-testid="bulk-generate-save">Generate</Button>
          </div>
        </div>
      )}
    </div>
  );
}
